'use client';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Check, Crown, Star, Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { useSubscription, PLAN_DETAILS, type PlanId } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { ALLOW_PUBLIC_REGISTRATION } from '@/lib/registrationConfig';
import {
  CHECKOUT_PLAN_STORAGE_KEY,
  isPaidPlanId,
} from '@/lib/safeRedirect';

import MarketingNav from '@/components/MarketingNav';
import MarketingFooter from '@/components/MarketingFooter';

/** Public paid launch: Creator and Studio only (no Free plan card). */
const PLAN_LIST = [
  {
    id: 'creator' as PlanId,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
    icon: Star,
    featureKeys: [
      'creatorFeature1',
      'creatorFeature2',
      'creatorFeature3',
      'creatorFeature4',
      'creatorFeature5',
      'creatorFeature6',
    ],
    highlight: true,
    badge: 'Most Popular',
  },
  {
    id: 'studio' as PlanId,
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/30',
    icon: Crown,
    featureKeys: [
      'studioFeature1',
      'studioFeature2',
      'studioFeature3',
      'studioFeature4',
      'studioFeature5',
      'studioFeature6',
    ],
    highlight: false,
  },
];

function PricingPageContent() {
  const { user } = useAuth();
  const { subscription, loading, startCheckout, openBillingPortal } = useSubscription();
  const [checkoutLoading, setCheckoutLoading] = useState<PlanId | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const { t } = useLanguage();
  const searchParams = useSearchParams();
  const pp = t?.pricingPage;
  const pf = t?.pricingPlans;

  const isTestMode = process.env.NEXT_PUBLIC_TEST_MODE === 'true';
  const paymentRequired = searchParams?.get('access') === 'payment-required';
  const autoCheckoutStarted = useRef(false);

  // Only treat a plan as "current" when:
  // 1. User is logged in
  // 2. Subscription data has loaded (not still fetching)
  // 3. Subscription status is active or trialing
  // 4. A real Stripe subscription ID exists (NULL IDs = broken/fake row, ignore it)
  const activeStatuses = ['active', 'trialing'];
  const hasRealStripe = !!(subscription?.stripeSubscriptionId);
  const isSubscriptionActive = !!(
    subscription &&
    hasRealStripe &&
    activeStatuses.includes(subscription.status) &&
    subscription.plan !== 'free'
  );
  const currentPlan: PlanId = isSubscriptionActive ? subscription!.plan : 'free';

  // Resume Creator/Studio selection after login/signup (query or sessionStorage).
  // Never grants workspace access — checkout + webhook do.
  useEffect(() => {
    if (!user || loading || checkoutLoading) return;
    if (isSubscriptionActive) return;
    if (autoCheckoutStarted.current) return;

    const fromQuery = searchParams?.get('plan');
    let plan: 'creator' | 'studio' | null = isPaidPlanId(fromQuery) ? fromQuery : null;
    if (!plan) {
      try {
        const stored = sessionStorage.getItem(CHECKOUT_PLAN_STORAGE_KEY);
        if (isPaidPlanId(stored)) plan = stored;
      } catch {
        /* ignore */
      }
    }
    if (!plan) return;

    autoCheckoutStarted.current = true;
    let cancelled = false;
    (async () => {
      setCheckoutLoading(plan!);
      try {
        sessionStorage.removeItem(CHECKOUT_PLAN_STORAGE_KEY);
        await startCheckout(plan!);
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to start checkout.';
        toast.error(msg);
      } finally {
        if (!cancelled) setCheckoutLoading(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally once when user + subscription settle with a pending plan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, isSubscriptionActive]);

  const handleUpgrade = async (planId: PlanId) => {
    if (!user) {
      const authTab = ALLOW_PUBLIC_REGISTRATION ? 'signup' : 'login';
      if (planId === 'creator' || planId === 'studio') {
        try {
          sessionStorage.setItem(CHECKOUT_PLAN_STORAGE_KEY, planId);
        } catch {
          /* ignore */
        }
        window.location.href = `/sign-up-login-screen?redirectTo=${encodeURIComponent(`/pricing?plan=${planId}`)}&plan=${planId}&tab=${authTab}`;
        return;
      }
      window.location.href = `/sign-up-login-screen?redirectTo=/pricing&tab=${authTab}`;
      return;
    }
    if (planId === 'free') return;
    setStripeError(null);
    setCheckoutLoading(planId);
    try {
      await startCheckout(planId as 'creator' | 'studio');
    } catch (err: any) {
      const msg: string = err?.message || 'Failed to start checkout. Please try again.';
      // Show inline banner for Stripe config errors instead of just a toast
      if (msg.toLowerCase().includes('stripe') || msg.toLowerCase().includes('test mode')) {
        setStripeError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setStripeError(null);
    try {
      await openBillingPortal();
    } catch (err: any) {
      const msg: string = err?.message || 'Failed to open billing portal.';
      if (msg.toLowerCase().includes('stripe') || msg.toLowerCase().includes('test mode')) {
        setStripeError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      <section className="py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-14">
            <p className="text-xs font-600 uppercase tracking-widest text-primary mb-3">{pp?.label ?? 'Pricing'}</p>
            <h1 className="text-hero-md font-700 text-foreground mb-4">
              {pp?.title ?? 'Simple, Transparent Pricing'}
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto text-base">
              {pp?.subtitle ??
                'Choose Creator or Studio to unlock your workspace. Cancel anytime. No hidden fees.'}
            </p>
          </div>

          {paymentRequired && (
            <div className="max-w-2xl mx-auto mb-8 flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/10 px-5 py-4 text-sm text-foreground">
              <AlertTriangle size={18} className="shrink-0 mt-0.5 text-primary" />
              <div>
                <p className="font-600 mb-0.5">Payment required</p>
                <p className="text-muted-foreground">
                  Choose Creator or Studio to unlock the workspace. Your account stays signed in —
                  complete checkout to continue.
                </p>
              </div>
            </div>
          )}

          {/* Stripe config error banner */}
          {stripeError && (
            <div className="max-w-2xl mx-auto mb-8 flex items-start gap-3 rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-5 py-4 text-sm text-yellow-700 dark:text-yellow-300">
              <AlertTriangle size={18} className="shrink-0 mt-0.5 text-yellow-500" />
              <div>
                <p className="font-600 mb-0.5">Stripe not configured</p>
                <p>{stripeError}</p>
                {isTestMode && (
                  <p className="mt-1 text-xs opacity-80">
                    Billing configuration is incomplete in this environment. Contact support if this persists.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Creator + Studio only — subscription badge/button resolves once loaded */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {PLAN_LIST.map((plan) => {
              const PlanIcon = plan.icon;
              const planData = PLAN_DETAILS[plan.id];
              // Only mark as current once loading is done and user is logged in
              const isCurrent = !loading && !!user && currentPlan === plan.id;
              const isLoading = checkoutLoading === plan.id;
              // Determine if this plan is an upgrade from the current plan
              const planOrder: Record<PlanId, number> = { free: 0, creator: 1, studio: 2 };
              const isUpgrade = planOrder[plan.id] > planOrder[currentPlan];

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-7 flex flex-col gap-5 transition-all duration-200
                    ${plan.highlight
                      ? 'glass-card border-2 border-primary/40 scale-[1.02]'
                      : 'glass-card border border-border'}
                    ${isCurrent ? plan.border : ''}
                  `}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 rounded-full text-xs font-700 bg-primary text-white">
                        {t?.pricing?.mostPopular ?? plan.badge}
                      </span>
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute top-3 right-3">
                      <span className="text-[10px] font-700 px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                        {pp?.yourPlan ?? 'Your Plan'}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${plan.bg} flex items-center justify-center`}>
                      <PlanIcon size={18} className={plan.color} />
                    </div>
                    <div>
                      <p className="font-700 text-foreground">{planData.name}</p>
                      <p className="text-xs text-muted-foreground">{planData.credits} {t?.pricing?.creditsPerMonth ?? 'credits/month'}</p>
                    </div>
                  </div>

                  <div>
                    <span className="text-4xl font-800 text-foreground tabular-nums">{planData.price}</span>
                    <span className="text-sm text-muted-foreground">{planData.period}</span>
                  </div>

                  <ul className="flex flex-col gap-2.5 flex-1">
                    {plan.featureKeys.map((key) => (
                      <li key={key} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                        <Check size={14} className="text-mint shrink-0" />
                        {pf?.[key as keyof typeof pf] ?? key}
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <button
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      className="btn-secondary px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2"
                    >
                      {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                      {pp?.manageBilling ?? 'Manage Billing'}
                    </button>
                  ) : isUpgrade || currentPlan === 'free' ? (
                    <>
                      <button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={isLoading || !!checkoutLoading}
                        className={`px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60
                          ${plan.id === 'studio' ? 'btn-accent' : 'btn-primary'}
                        `}
                      >
                        {isLoading ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : user ? (
                          `${pp?.upgradeTo ?? 'Upgrade to'} ${planData.name}`
                        ) : (
                          `${pp?.startPlan ?? 'Choose'} ${planData.name}`
                        )}
                      </button>
                      <p className="text-[11px] text-muted-foreground/70 text-center leading-relaxed">
                        By subscribing, you agree to our{' '}
                        <a href="/terms-of-service" className="underline hover:text-muted-foreground transition-colors">Terms of Service</a>
                        {' '}and acknowledge our{' '}
                        <a href="/privacy-policy" className="underline hover:text-muted-foreground transition-colors">Privacy Policy</a>.
                      </p>
                    </>
                  ) : (
                    // Downgrade case (e.g. Studio user viewing Creator)
                    <button
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      className="btn-secondary px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2"
                    >
                      {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                      {pp?.manageBilling ?? 'Manage Billing'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* FAQ */}
          <div className="max-w-2xl mx-auto mt-20">
            <h2 className="text-xl font-700 text-foreground text-center mb-8">{pp?.faqTitle ?? 'Frequently Asked Questions'}</h2>
            <div className="flex flex-col gap-4">
              {[
                { q: pp?.faq1q ?? 'Can I cancel anytime?', a: pp?.faq1a ?? 'Yes. Cancel anytime from the billing portal. Your plan stays active until the end of the billing period.' },
                { q: pp?.faq2q ?? 'What happens to my credits if I downgrade?', a: pp?.faq2a ?? 'Your credits reset to the new plan limit at the start of your next billing cycle.' },
                { q: pp?.faq3q ?? 'Do unused credits roll over?', a: pp?.faq3a ?? 'Credits reset monthly and do not roll over. Use them before your billing cycle ends.' },
                {
                  q: pp?.faq4q ?? 'Do I need a paid plan to use the workspace?',
                  a:
                    pp?.faq4a ??
                    'Yes. Choose Creator ($12/month, 100 credits) or Studio ($39/month, 500 credits) to unlock the workspace after signup.',
                },
              ].map((item) => (
                <div key={item.q} className="glass-card rounded-xl p-5 border border-border">
                  <p className="text-sm font-600 text-foreground mb-1.5">{item.q}</p>
                  <p className="text-sm text-muted-foreground">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

export default function PricingPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="animate-spin text-primary" size={24} />
        </div>
      }
    >
      <PricingPageContent />
    </Suspense>
  );
}
